import type { TFunction } from 'i18next'
import type { ControllerConfig, ValueState } from '../../types'

import { Operators } from '@/shared/components/query-builder'

import { isObject, objectMap } from '../../utils'
import FieldController from '../Controller'

export default class NumberController extends FieldController {
	constructor(config: ControllerConfig) {
		super(config)
		this.note = config.note
		this.validate = config.validate || this.defaultValidate
	}

	note?: string

	formatLabel = () => this.label

	hasValue = ({ operation, value }: ValueState) => {
		if (operation === Operators.IS_EMPTY) return true

		let bool = typeof value === 'number'

		if (isObject(value)) {
			Object.values(value).forEach((v) => {
				bool = typeof v === 'number'
			})
		}

		return bool
	}

	getInitialValue = () => ({
		operation: Operators.EQ,
		value: '',
	})

	getFilterTypes = (t: TFunction) => [
		{
			operation: Operators.EQ,
			label: t('eq.long', { defaultValue: 'equal' }),
			hasInput: true,
		},
		{
			operation: Operators.NE,
			label: t('ne.long', { defaultValue: 'not equal' }),
			hasInput: true,
		},
		{
			operation: Operators.GT,
			label: t('gt.long', { defaultValue: 'greater than' }),
			hasInput: true,
		},
		{
			operation: Operators.GTE,
			label: t('gte.long', { defaultValue: 'greater or equal than' }),
			hasInput: true,
		},
		{
			operation: Operators.LT,
			label: t('lt.long', { defaultValue: 'less than' }),
			hasInput: true,
		},
		{
			operation: Operators.LTE,
			label: t('lte.long', { defaultValue: 'less or equal than' }),
			hasInput: true,
		},
		{
			operation: Operators.BETWEEN,
			label: t('num_between.long', { defaultValue: 'between' }),
			hasInput: true,
		},
		{
			operation: Operators.IS_EMPTY,
			label: t('is_empty.long', { defaultValue: 'has no value' }),
		},
		{
			operation: Operators.IS_NOT_EMPTY,
			label: t('is_not_empty.long', { defaultValue: 'has value' }),
		},
	]

	defaultValidate = (
		{
			operation,
			value,
		}:
			| ValueState<{
					lt: number
					gt: number
			  }>
			| ValueState<string>,
		t: TFunction
	) => {
		let result: string | null = null
		const nameMap = { lt: 'to', gt: 'from' }

		if (operation === Operators.IS_EMPTY) {
			return result
		}

		if (isObject(value) && value) {
			const betweenValue = value as {
				lt: number
				gt: number
			}
			// make sure both values are present
			if (betweenValue.lt === undefined || betweenValue.gt === undefined) {
				return t('validation.both_inputs_required', {
					defaultValue: 'Both inputs are required.',
				})
			}
			if (betweenValue.lt < 0 || betweenValue.gt < 0) {
				return t('validation.both_only_positive', {
					defaultValue: 'Only positive values are available.',
				})
			}

			// check for a valid range
			if (betweenValue.lt <= betweenValue.gt) {
				return t('validation.invalid_between_range', {
					defaultValue:
						'Invalid range; the second input must be greater than the first.',
				})
			}

			objectMap(betweenValue, (val: string, key: string) => {
				const r = validateInput(this.label, val, t, nameMap[key as 'lt' | 'gt'])
				if (r) result = r

				return null
			})
		} else {
			result = validateInput(this.label, value as string, t)
		}

		return result
	}
}

const validateInput = (
	label: string,
	_value: string,
	t: TFunction,
	name?: string
) => {
	let result: string | null = null
	const prefix = name ? `${label} "${name}"` : label
	const value = Number(_value)
	if (_value === '' || _value === null || _value === undefined) {
		result = `${prefix} ${t('validation.must_be_number', { defaultValue: 'must be a number' })}`
	} else if (Number.isNaN(value)) {
		result = `${prefix} ${t('validation.must_be_number', { defaultValue: 'must be a number' })}`
	} else if (!Number.isFinite(value)) {
		result = `${prefix} ${t('validation.must_be_whole_number', { defaultValue: 'must be a whole number' })}`
	}

	return result
}
